import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from './Input';
import { SelectCountry } from './SelectCountry';
import { InputTerms } from './InputTerms';
import { liquidationprojectlead } from '@/lib/repository/crm/CRMRepository';
import { Text } from '@/components/layout/CoorpLayout';
import Image from 'next/image';
import benedikt from '~/images/Benedikt.png';
import { FaEnvelope, FaPhone } from 'react-icons/fa';
import { pushDataLayer } from '@/lib/utilities/analytics/gtm';
import { useRouter } from 'next-translate-routes-multi-domain';
import { bingUET } from '@/lib/utilities/analytics/bing';
import { SuccessCheck } from './Success/SuccessCheck';
import useUserMetadata from '@/lib/hooks/useUserData';
import { useUser } from '@auth0/nextjs-auth0/client';
import { hasPhonePrefix } from '@/lib/data-transformer/gindumacUtils';
import COUNTRY_NAMES from '@/lib/data/country-names.json';
import { LinkEditMetadata } from '../ui/Links/LinkEditMetadata';

type FormInputs = {
  'we-buy-your-project_company': string;
  'we-buy-your-project_email': string;
  'we-buy-your-project_first-name': string;
  'we-buy-your-project_last-name': string;
  'we-buy-your-project_city': string;
  'we-buy-your-project_country': string;
  'we-buy-your-project_phone-number': string;
  'we-buy-your-project_phone-prefix': string;
  'we-buy-your-project_message': string;
  'we-buy-your-project_terms': boolean;
};

function WeBuyYourProjectForm() {
  const { t } = useTranslation('products');
  const { locale } = useRouter();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitSuccessful, isSubmitting },
  } = useForm<FormInputs>();
  async function onSubmit(data: FormInputs) {
    console.log('data', data);
    try {
      await liquidationprojectlead({
        'platform-name': 'GINDUMAC Website',
        'platform-type': '"We Buy Your Project" Request',
        country: data['we-buy-your-project_country'],
        city: data['we-buy-your-project_city'],
        message: data['we-buy-your-project_message'],
        'phone-number': data['we-buy-your-project_phone-number'],
        'phone-prefix': data['we-buy-your-project_phone-prefix'],
        email: data['we-buy-your-project_email'],
        company: data['we-buy-your-project_company'],
        'first-name': data['we-buy-your-project_first-name'],
        'last-name': data['we-buy-your-project_last-name'],
      });
      trackConversion();
    } catch (error) {
      console.error('error', error);
    }
  }

  function trackConversion() {
    /*
        gtag('event', 'conversion', {
      send_to: 'AW-772487181/XrdoCI_ZnZMBEI3wrPAC',
      transaction_id: '',
      */
    pushDataLayer('formSubmission', {
      formType: 'We Buy Your Project',
      locale: locale,
    });
    bingUET('Contact Form QM');
  }

  const { metadata } = useUserMetadata();
  const { user } = useUser();
  const [hasPrefixInput, setHasPrefixInput] = useState(false);
  useEffect(() => {
    function handleReset() {
      const phone = metadata?.phone ?? metadata?.companyPhone;
      setHasPrefixInput(!hasPhonePrefix(phone));
      reset({
        'we-buy-your-project_company': metadata?.companyName,
        'we-buy-your-project_email': user?.email ?? metadata?.companyEmail,
        'we-buy-your-project_last-name': metadata?.lastName,
        'we-buy-your-project_city': metadata?.city ?? metadata?.companyCity,
        'we-buy-your-project_phone-number': phone,
        'we-buy-your-project_country': COUNTRY_NAMES.find(
          (e) => e[1] === (metadata?.country ?? metadata?.companyCountry)
        )?.[0],
      });
    }
    // run handleReset after one sec
    const timer = setTimeout(() => handleReset(), 1000);
    return () => clearTimeout(timer);
  }, [metadata, reset, user?.email, setHasPrefixInput]);

  return (
    <div className='flex justify-center bg-[#f2f2f2] p-5'>
      <div className='container xl:max-w-[80%]'>
        <div>
          <div>
            <div>
              {!isSubmitSuccessful && (
                <>
                  <strong className='flex justify-center text-[1.3em] font-bold uppercase text-[#4d749b] lg:text-[1.8em]'>
                    {t('DETAIL_ACCMANAGER_CONTACT')}
                  </strong>
                  <Text text={t('GET_IN_CONTACT_MESSAGE')} />
                  <div className='my-10 flex flex-col items-center justify-center md:flex-row md:p-0'>
                    <div className='m-2 flex overflow-hidden rounded-full border-4 border-brand-500'>
                      <Image
                        src={benedikt}
                        alt='Benedikt Ruf'
                        width={210}
                        height={210}
                      />
                    </div>
                    <div className='flex flex-col items-center md:ml-16'>
                      <h3 className='px-8 py-6 font-medium md:px-0 md:text-3xl'>
                        Benedikt Ruf
                      </h3>
                      <h4 className='px-5 py-2 font-thin md:px-0 md:text-[1.35em]'>
                        Co-CEO & Managing Partner
                      </h4>
                      <p className='flex items-center space-x-2 px-6 py-2 text-sm font-semibold md:px-0 md:text-lg'>
                        <FaPhone className='mr-2 rotate-90' />
                        +49 173 273 19 73
                      </p>
                      <p className='flex items-center space-x-2 px-4 py-2 text-sm font-semibold md:px-0 md:text-lg'>
                        <FaEnvelope className='mr-2' />
                        b.ruf@gindumac.com
                      </p>
                    </div>
                  </div>
                </>
              )}
              <div className=''>
                <form
                  id='account-manager_form'
                  onSubmit={handleSubmit(onSubmit)}
                >
                  {isSubmitSuccessful ? (
                    <SuccessCheck />
                  ) : (
                    <>
                      <h4 className='mb-4 uppercase'>
                        {t('DETAIL_ACCMANAGER_CONTACT')}
                      </h4>
                      <div className='mb-4 w-full'>
                        <LinkEditMetadata />
                      </div>
                      <div className='flex space-x-4 pb-4'>
                        <div className='w-1/2'>
                          <Input
                            placeholder={t('FORM_PLACEHOLDER_COMPANY')}
                            name='we-buy-your-project_company'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                        <div className='w-1/2'>
                          <Input
                            placeholder={t('FORM_PLACEHOLDER_EMAIL')}
                            name='we-buy-your-project_email'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </div>
                      <div className='flex space-x-4 pb-4'>
                        <div className='w-1/2'>
                          <Input
                            placeholder={t('FORM_PLACEHOLDER_FIRSTNAME')}
                            name='we-buy-your-project_first-name'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                        <div className='w-1/2'>
                          <Input
                            placeholder={t('FORM_PLACEHOLDER_LASTNAME')}
                            name='we-buy-your-project_last-name'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </div>
                      <div className='flex space-x-4 pb-4'>
                        <div className='w-1/2'>
                          <Input
                            placeholder={t(
                              'FORM_PLACEHOLDER_CITY'
                            ).toUpperCase()}
                            name='we-buy-your-project_city'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                        {/* A select input with a floating label */}
                        <div className='w-1/2'>
                          <SelectCountry
                            type='country'
                            name='we-buy-your-project_country'
                            register={register}
                            errors={errors}
                            validation={{
                              required: true,
                            }}
                          />
                        </div>
                      </div>
                      <div className='flex space-x-4 pb-4'>
                        {hasPrefixInput && (
                          <div className='relative w-1/2'>
                            <SelectCountry
                              type='phoneCode'
                              name='we-buy-your-project_phone-prefix'
                              value={watch('we-buy-your-project_phone-prefix')}
                              errors={errors}
                              register={register}
                              validation={{
                                required: true,
                              }}
                            />
                          </div>
                        )}

                        <div className={hasPrefixInput ? 'w-1/2' : 'w-full'}>
                          <Input
                            placeholder={t('FORM_PLACEHOLDER_PHONE')}
                            name='we-buy-your-project_phone-number'
                            validation={{
                              required: true,
                              maxLength: 80,
                            }}
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </div>
                      <div className='flex space-x-4 pb-4'>
                        <Input
                          type='textarea'
                          placeholder={t(
                            'FORM_PLACEHOLDER_MESSAGE'
                          ).toUpperCase()}
                          name='we-buy-your-project_message'
                          validation={{
                            required: true,
                            maxLength: 2500,
                          }}
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <div className='mb-4'>
                        <div className='input-field gd-input-field col l9 m9 s10'>
                          <InputTerms
                            name='we-buy-your-project_terms'
                            register={register}
                            errors={errors}
                          />
                        </div>
                      </div>
                      <div className=''>
                        <div className='flex justify-end'>
                          <button
                            className='rounded bg-brand-500 px-16 py-4 text-sm uppercase tracking-wider text-white md:px-24'
                            type='submit'
                            disabled={isSubmitting}
                          >
                            {t('FORM_BTN_SEND')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeBuyYourProjectForm;
